import { useEffect, useCallback, useRef } from "react";
import { env } from "../../../shared/config/env";
import { useNavigate } from "react-router-dom";
import { useFormDataStore } from "../store/formDataStore";
import { WebSocketMessage } from "../types";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { getAccessToken } from "@/shared/api/clients/tokenAccessor";
import { SaveCancelledError } from "./useReportSaving";

// 30s transport keepalive. Deliberately does NOT refresh the server lock TTL —
// the server ignores heartbeats for inactivity tracking (CJIS); only real user
// activity (edits + the interaction pings below) resets the editing timeout.
const HEARTBEAT_INTERVAL_MS = 30_000;

// Any user interaction on the edit page resets the server-side editing
// timeout, not just edits — otherwise someone reading a report or presenting
// it (sales demos) gets the inactivity warning and is kicked out while
// visibly using the page. Pings are throttled: at most one per interval, sent
// on the $default route so the server's logAction handler records activity
// and pushes the lock TTL out.
//
// Only events the browser generates from real input hardware are listed.
// "scroll" is deliberately absent: programmatic scrolls (the transcript
// overlay auto-scrolling during video playback, chat autoscroll) emit
// trusted scroll events indistinguishable from a user's, which would renew
// the CJIS inactivity lock with nobody present. Every human way of scrolling
// already surfaces here as wheel / pointer / key / touch events.
const ACTIVITY_PING_INTERVAL_MS = 60_000;
const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
] as const;

interface UseReportWebSocketProps {
  liveWebSocket: React.MutableRefObject<WebSocket | null>;
  reportID: string | undefined;
  enableEditing: boolean;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showTimeoutWarning: boolean;
  setWarningState: (state: boolean) => void;
  setInactivityExpiryTimestamp: (timestamp: number) => void;
  refetchIncident: () => void;
  setIsSaving: (saving: boolean) => void;
  saveDeferredRef: React.MutableRefObject<{
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
    timeoutId: NodeJS.Timeout | null;
  } | null>;
  setLastModifiedTime: (date: Date | null) => void;
  setShowRMSCommit: (show: boolean) => void;
  setReportType: (type: string) => void;
  setVideoData: (data: VideoData | VideoData[] | null) => void;
  setWarnings: (warnings: string[]) => void;
  /**
   * True while a full-report run is regenerating this report. The backend
   * takes the report lock during the run, so we proactively close the editing
   * socket and suppress its disconnect-driven redirects — the officer stays on
   * the page watching the progress timer until the run completes.
   */
  isGeneratingFull?: boolean;
}

export const useReportWebSocket = ({
  liveWebSocket,
  reportID,
  enableEditing,
  showError,
  showSuccess,
  showTimeoutWarning,
  setWarningState,
  setInactivityExpiryTimestamp,
  refetchIncident,
  setIsSaving,
  saveDeferredRef,
  setLastModifiedTime,
  setShowRMSCommit,
  setReportType,
  setVideoData,
  setWarnings,
  isGeneratingFull = false,
}: UseReportWebSocketProps) => {
  const navigate = useNavigate();

  // Read inside the long-lived WS event handlers without re-subscribing them.
  // When true, a socket close is expected (full-report regeneration) and must
  // not trigger any redirect.
  const suppressRedirectRef = useRef(false);
  suppressRedirectRef.current = isGeneratingFull;

  // Ref to hold latest callback props — avoids stale closures in WS event handlers
  const handlersRef = useRef({
    showError, showSuccess, showTimeoutWarning, setWarningState,
    setInactivityExpiryTimestamp, refetchIncident, setIsSaving,
    setLastModifiedTime, setShowRMSCommit, setReportType,
    setVideoData, setWarnings,
  });
  handlersRef.current = {
    showError, showSuccess, showTimeoutWarning, setWarningState,
    setInactivityExpiryTimestamp, refetchIncident, setIsSaving,
    setLastModifiedTime, setShowRMSCommit, setReportType,
    setVideoData, setWarnings,
  };

  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Latched synchronously when a sessionHeartbeatWarning arrives, cleared only
  // by an explicit Continue or by a server response to a real user action
  // (which renewed the lock server-side). The activity pings gate on this ref
  // rather than on the showTimeoutWarning prop alone: the prop only updates on
  // the next render, so a ping could otherwise slip through in the gap after
  // the warning arrives — silently renewing the session the modal is asking
  // the user to explicitly continue.
  const inactivityWarningActiveRef = useRef(false);

  useEffect(() => {
    // Guards against a race where isGeneratingFull flips true (or the effect
    // otherwise tears down) while getAccessToken() is still pending. At that
    // point liveWebSocket.current is still null, so cleanup can't close the
    // socket — without this flag the resumed connect() would create a stale
    // socket during full-report regeneration.
    let cancelled = false;

    // While a full-report run is in flight the backend owns the report lock,
    // so hold no editing socket. The previous run's cleanup already closed it;
    // stay disconnected here and reconnect once generation finishes (this
    // effect re-runs when isGeneratingFull flips back to false).
    if (isGeneratingFull) {
      return;
    }

    // Connect to live editing socket
    if (liveWebSocket.current === null && enableEditing && reportID) {
      let lastCloseCode: number | null = null;

      const connect = async () => {
        let tokenParam = "";
        try {
          const token = await getAccessToken();
          tokenParam = `&token=${encodeURIComponent(token)}`;
        } catch {
          // Token not available — connect without it; server will reject if required
        }
        // Effect was cleaned up while the token request was in flight — abort
        // before creating the socket so we never reconnect during teardown.
        if (cancelled) {
          return;
        }
        const wsURL = `${env.websocketsDomain}?reportID=${reportID}${tokenParam}`;

        const editingSocket = new WebSocket(wsURL);

      editingSocket.onopen = () => {
        console.log("[WebSocket] Connected");
        // Start the client-side transport keepalive (does not renew the
        // server's editing-lock TTL — only real user activity does)
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
        }
        heartbeatIntervalRef.current = setInterval(() => {
          if (editingSocket.readyState === WebSocket.OPEN) {
            editingSocket.send(JSON.stringify({ action: "heartbeat" }));
          }
        }, HEARTBEAT_INTERVAL_MS);
      };

      editingSocket.onerror = (event) => {
        console.error("[WebSocket] Connection error:", event);
      };

      editingSocket.onclose = (event) => {
        console.log("[WebSocket] Closed:", event.code, event.reason);
        // Stop heartbeat on close
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }

        // Expected teardown during full-report regeneration: the backend holds
        // the lock and closes us. Stay on the page (no redirect).
        if (suppressRedirectRef.current) {
          return;
        }

        // Intentional teardown: the effect's cleanup set `cancelled` and called
        // close() itself (component unmounting / navigating away, e.g. after the
        // officer clicks "Finish Review" or signs). The resulting close is not a
        // real disconnect — don't show an error or redirect the user home. Only
        // closes that happen while the editor is still live (cancelled === false)
        // represent a genuine drop worth surfacing.
        if (cancelled) {
          return;
        }

        const actualCode = lastCloseCode || event.code;
        if (actualCode === 3008) {
          navigate(`/`, {
            replace: true,
            state: { message: "You were redirected due to inactivity." },
          });
        } else if (actualCode === 3009) {
          handlersRef.current.showError(
            "Another user is currently editing this report. Please try again later."
          );
          navigate("/");
        } else if (actualCode === 3002) {
          handlersRef.current.showError("User is not authenticated. Please login again.");
          navigate("/login");
        } else if (actualCode === 3007) {
          handlersRef.current.showError(
            "Lock was lost. Another user may have accessed this report."
          );
          navigate("/");
        } else if (actualCode === 1006) {
          handlersRef.current.showError(
            "Connection to server lost unexpectedly. Please refresh the page to continue editing."
          );
          navigate("/", { replace: true });
        }
      };

      editingSocket.onmessage = async (event) => {
        // Ignore anything arriving during full-report regeneration — the
        // socket is being torn down and some branches below redirect / wipe
        // the store, which must not happen while the officer waits on the
        // progress timer.
        if (suppressRedirectRef.current) {
          return;
        }

        const data: WebSocketMessage = JSON.parse(event.data);

        // Server-side transport keepalive (heartbeatWorker). It carries no
        // user-facing state and — critically — keeps arriving after an
        // inactivity warning, so letting it fall through would auto-dismiss
        // the warning modal below without any lock renewal, defeating the
        // explicit-Continue requirement.
        if (data.type === "ping") {
          return;
        }

        if (data.closeCode) {
          lastCloseCode = data.closeCode;
        }

        if (data.closing && data.closeCode) {
          handlersRef.current.showError(data.error || "Connection closing");
          return;
        }

        if (data.type === "sessionHeartbeatWarning") {
          inactivityWarningActiveRef.current = true;
          if (!handlersRef.current.showTimeoutWarning && data.expires) {
            handlersRef.current.setInactivityExpiryTimestamp(data.expires);
            handlersRef.current.setWarningState(true);
          }
          return;
        }

        if (data.status === "duplicate") {
          const { destroyStore } = useFormDataStore.getState();
          destroyStore();
          handlersRef.current.showError(
            data.error ||
              "Another user is currently editing this report. Please try again later."
          );
          navigate("/", { replace: true });
          return;
        }

        // Any other server message is a response to a real user action (the
        // ping above never reaches here) — dismiss the warning modal.
        if (handlersRef.current.showTimeoutWarning) {
          handlersRef.current.setWarningState(false);
        }

        if (data.error) {
          handlersRef.current.showError(data.error);
          handlersRef.current.setIsSaving(false);
          if (saveDeferredRef.current) {
            if (saveDeferredRef.current.timeoutId) {
              clearTimeout(saveDeferredRef.current.timeoutId);
            }
            saveDeferredRef.current.reject(new Error(data.error));
            saveDeferredRef.current = null;
          }
          return;
        }

        // Only non-error responses prove the lock was renewed server-side:
        // every handler renews before pushing state, but error pushes can
        // come from a failed validateLock (no renewal) — so the activity-ping
        // latch lifts here, after the error early-return above.
        inactivityWarningActiveRef.current = false;

        if (data.success) {
          handlersRef.current.setIsSaving(false);
          handlersRef.current.showSuccess(data.success);

          if (saveDeferredRef.current) {
            if (saveDeferredRef.current.timeoutId) {
              clearTimeout(saveDeferredRef.current.timeoutId);
            }
            saveDeferredRef.current.resolve();
            saveDeferredRef.current = null;
          }

          if (data.narrativeUpdated) {
            handlersRef.current.refetchIncident();
          }
        }

        if (data.exit) {
          const { destroyStore } = useFormDataStore.getState();
          destroyStore();

          if (data.timeout) {
            navigate(`/`, {
              replace: true,
              state: { message: "You were redirected due to inactivity." },
            });
          } else {
            navigate("/");
          }
          return;
        }

        if (data.newObject) {
          const { setFormData } = useFormDataStore.getState();
          setFormData(data.newObject);
          handlersRef.current.setLastModifiedTime(
            data.lastModified ? new Date(data.lastModified) : new Date()
          );
        }

        if (typeof data.supportsRMS !== "undefined") {
          handlersRef.current.setShowRMSCommit(data.supportsRMS);
        }

        if (data.reportType) {
          handlersRef.current.setReportType(data.reportType);
        }

        if (data.video) {
          handlersRef.current.setVideoData(data.video);
        }

        if (data.warnings && data.warnings.length > 0) {
          handlersRef.current.setWarnings(data.warnings);
        }
      };

        liveWebSocket.current = editingSocket;
      };

      connect();
    }

    if (
      liveWebSocket.current &&
      liveWebSocket.current.readyState === WebSocket.OPEN
    ) {
        // Log or something
    }

    return () => {
      // Cleanup
      console.log("Cleaning up websocket connection");
      // Cancel any in-flight connect() so it won't create a socket after teardown.
      cancelled = true;
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }

      // Cancel any pending save. Its server ack can never arrive on the socket
      // we're about to close (the officer navigated away from the edit page, or
      // the socket is reconnecting), so clear the 15s timeout and reject the
      // deferred with SaveCancelledError. Awaiters (submit / generate-full)
      // treat that as a silent abort; without this the stale timeout would fire
      // later and surface a spurious "could not be saved" toast after the user
      // has already left.
      if (saveDeferredRef.current) {
        if (saveDeferredRef.current.timeoutId) {
          clearTimeout(saveDeferredRef.current.timeoutId);
        }
        saveDeferredRef.current.reject(new SaveCancelledError());
        saveDeferredRef.current = null;
        handlersRef.current.setIsSaving(false);
      }

      liveWebSocket.current?.close();
      liveWebSocket.current = null;
    };
  }, [reportID, enableEditing, isGeneratingFull]);

  // Reset the server editing timeout on ANY user interaction (mouse, keys,
  // wheel, touch), throttled to one ping per ACTIVITY_PING_INTERVAL_MS.
  // Deliberately paused while the inactivity warning is active: by then the
  // user was genuinely idle, and continuing the session must be the modal's
  // explicit "Continue" click, not an incidental mouse twitch.
  const lastActivityPingRef = useRef(0);
  useEffect(() => {
    if (!enableEditing) return;

    const onUserActivity = (event: Event) => {
      // Only browser-generated input counts. Script-dispatched synthetic
      // events (extensions, "keep awake" tools, libraries) must not be able
      // to hold the CJIS inactivity lock open on an unattended session.
      if (!event.isTrusted) return;
      if (
        inactivityWarningActiveRef.current ||
        handlersRef.current.showTimeoutWarning
      ) {
        return;
      }
      const socket = liveWebSocket.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;

      const now = Date.now();
      if (now - lastActivityPingRef.current < ACTIVITY_PING_INTERVAL_MS) return;
      lastActivityPingRef.current = now;

      socket.send(JSON.stringify({ action: "User activity" }));
    };

    // Capture phase so component handlers that stopPropagation still count
    // as activity.
    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, onUserActivity, {
        capture: true,
        passive: true,
      });
    }
    return () => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, onUserActivity, {
          capture: true,
        });
      }
    };
  }, [enableEditing, liveWebSocket]);

  const handleUserAction = useCallback((action: string) => {
    if (showTimeoutWarning || !enableEditing) return;

    // Null ref means the connect is still starting up, or the editor was torn
    // down (navigation/unmount). Neither is a real connection loss — activity
    // logging is fire-and-forget, so drop it silently rather than alarming the
    // user with a spurious "connection lost" toast. A genuinely dropped socket
    // leaves the object present with readyState !== OPEN (handled below), and
    // unexpected closes are already surfaced + redirected by onclose.
    if (!liveWebSocket.current) return;

    if (liveWebSocket.current.readyState !== WebSocket.OPEN) {
      showError(
        "Connection to server lost. Please refresh the page to make edits."
      );
      return;
    }
    liveWebSocket.current.send(JSON.stringify({ action }));
  }, [showTimeoutWarning, enableEditing, showError]);

  const continueSession = useCallback(() => {
     inactivityWarningActiveRef.current = false;
     setWarningState(false);
     if (
      liveWebSocket.current &&
      liveWebSocket.current.readyState === WebSocket.OPEN
    ) {
      liveWebSocket.current.send(
        JSON.stringify({ action: "Continue session" })
      );
    }
  }, [setWarningState]);

  return { handleUserAction, continueSession };
};
